import { z } from 'zod';
import { monthSchema } from '../money/accounting-period.js';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { paymentMethodSchema } from '../money/payment-methods.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
} from './common.js';

/**
 * What the shop SPENDS, and the profit that falls out of setting it against what it earns.
 *
 * Two decisions are encoded here and neither is cosmetic.
 *
 * **An expense carries two dates.** `incurredOn` is when the cost arose; `paidOn` is when the
 * money actually left. For most rows a shop types they are the same day and the form only asks
 * once. They come apart exactly where a P&L basis comes apart: a fabric bill received on the
 * 28th and settled on the 3rd is August's cost and September's outflow. Storing one date and
 * calling the toggle "cash versus accrual" would be a label on nothing.
 *
 * **A system-generated expense is not editable.** When a staff payout is settled (task 21) the
 * wages appear here automatically. That row is a shadow of the payout, and letting somebody
 * retype its amount would make the P&L and the payout ledger disagree with no trail of which is
 * right. It is corrected by correcting the payout.
 */

// --- Categories --------------------------------------------------------------------------------

export const EXPENSE_CATEGORY_LABEL_MAX_LENGTH = 60;
export const EXPENSE_VENDOR_MAX_LENGTH = 120;
export const EXPENSE_NOTE_MAX_LENGTH = 500;

/**
 * What a boutique actually spends on, in the order an owner would think of it.
 *
 * These are COPIED into each shop at signup rather than referenced from a global list, exactly
 * as the measurement templates are: a shop that renames "Trims and accessories" to "Laces" is
 * editing its own row and nobody else sees it. The keys are the permanent identifiers and never
 * change; the labels are what a shop is free to rewrite.
 */
export const DEFAULT_EXPENSE_CATEGORIES = Object.freeze([
  { key: 'rent', label: 'Rent' },
  { key: 'electricity', label: 'Electricity' },
  { key: 'wages', label: 'Wages and payouts' },
  { key: 'fabric', label: 'Fabric and material purchase' },
  { key: 'trims', label: 'Trims and accessories' },
  { key: 'machine_maintenance', label: 'Machine maintenance' },
  { key: 'transport', label: 'Transport' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'miscellaneous', label: 'Miscellaneous' },
] as const);

export type DefaultExpenseCategoryKey = (typeof DEFAULT_EXPENSE_CATEGORIES)[number]['key'];

/** Where a settled staff payout files itself. Task 21 does not get to choose. */
export const PAYOUT_EXPENSE_CATEGORY_KEY: DefaultExpenseCategoryKey = 'wages';

/**
 * Where a settled JOB-WORK bill files itself (task 29).
 *
 * Also `wages`, and that is a considered choice rather than laziness: from the P&L's point of
 * view, money paid to an embroiderer for stitching a blouse is the same kind of cost as money
 * paid to the karigar at the next table for the same blouse. Splitting them would give an owner
 * two lines to add together every time they asked what labour cost them.
 */
export const JOB_WORK_EXPENSE_CATEGORY_KEY: DefaultExpenseCategoryKey = 'wages';

/**
 * Where a supplier's bill files itself (task 28).
 *
 * `fabric`, NOT `wages`, and the distinction is the one an owner reads the P&L for: cloth bought
 * is a cost of what the shop sells, and wages are a cost of making it. Putting both under one
 * line would leave the two questions an owner actually asks — "what did material cost me?" and
 * "what did labour cost me?" — sharing a single, useless number.
 */
export const SUPPLIER_EXPENSE_CATEGORY_KEY: DefaultExpenseCategoryKey = 'fabric';

export const expenseCategorySchema = z.object({
  id: objectIdSchema,
  /** Stable slug. Seeded categories keep theirs forever; a shop's own gets one from its label. */
  key: z.string(),
  label: z.string(),
  /** True for the nine seeded rows — they may be renamed, never deleted. */
  seeded: z.boolean(),
  archivedAt: isoDateStringSchema.nullable(),
  sortOrder: z.number().int(),
});
export type ExpenseCategory = z.infer<typeof expenseCategorySchema>;

/** The picker, as the endpoint answers it. */
export const expenseCategoryListSchema = z.object({ items: z.array(expenseCategorySchema) });
export type ExpenseCategoryList = z.infer<typeof expenseCategoryListSchema>;

export const createExpenseCategorySchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, 'Give the category a name')
    .max(EXPENSE_CATEGORY_LABEL_MAX_LENGTH),
});
export type CreateExpenseCategoryInput = z.infer<typeof createExpenseCategorySchema>;

export const updateExpenseCategorySchema = z
  .object({
    label: z.string().trim().min(1).max(EXPENSE_CATEGORY_LABEL_MAX_LENGTH).optional(),
    /** Archiving hides a category from the picker without touching the rows filed under it. */
    archived: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
export type UpdateExpenseCategoryInput = z.infer<typeof updateExpenseCategorySchema>;

// --- One expense --------------------------------------------------------------------------------

/**
 * Where a row came from, which is what decides whether anyone may edit it.
 *
 *  - `manual`    somebody typed it.
 *  - `recurring` generated from a standing instruction and confirmed by the owner.
 *  - `payout`    written by task 21 when a karigar was settled. System-generated; read-only.
 */
/**
 * `job_work` joins `payout` as a system-generated source (task 29). Both are written by a
 * settlement and neither is editable through any expense route — the settlement is what gets
 * corrected, not its shadow in the expense book.
 */
export const EXPENSE_SOURCES = ['manual', 'recurring', 'payout', 'job_work', 'supplier'] as const;
export type ExpenseSource = (typeof EXPENSE_SOURCES)[number];
export const expenseSourceSchema = z.enum(EXPENSE_SOURCES);

/**
 * `pending` is a row the recurring generator produced and nobody has confirmed. It is NOT in the
 * P&L, is not in the expense list and is not in the export — an unconfirmed rent that silently
 * counted against profit is precisely the thing the brief refuses.
 */
export const EXPENSE_STATUSES = ['pending', 'posted'] as const;
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];
export const expenseStatusSchema = z.enum(EXPENSE_STATUSES);

/** The `entityType` a bill photograph is filed under in the media pipeline. */
export const EXPENSE_MEDIA_ENTITY_TYPE = 'expense';

export const expenseSchema = z.object({
  id: objectIdSchema,
  /** When the cost arose. The accrual basis counts a month by this. */
  incurredOn: isoDateStringSchema,
  /** When the money left. Null while a bill is received but unpaid. The cash basis counts this. */
  paidOn: isoDateStringSchema.nullable(),
  amountPaise: nonNegativePaiseSchema,
  categoryId: objectIdSchema,
  categoryKey: z.string(),
  /** Denormalised so a list of a hundred rows does not join a hundred times. */
  categoryLabel: z.string(),
  vendor: z.string().nullable(),
  method: paymentMethodSchema,
  note: z.string().nullable(),
  /** Media ids from the shared uploader — the photograph of the bill. */
  billMediaIds: z.array(objectIdSchema),
  source: expenseSourceSchema,
  status: expenseStatusSchema,
  /** True when the row belongs to something else and may not be edited on its own. */
  systemGenerated: z.boolean(),
  recurringExpenseId: objectIdSchema.nullable(),
  /** The staff payout this row shadows, when task 21 wrote it. */
  payoutId: objectIdSchema.nullable(),
  recordedByUserId: z.string().nullable(),
  recordedByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Expense = z.infer<typeof expenseSchema>;

export const recordExpenseSchema = z.object({
  /** At least one paisa: a zero-rupee expense is a mis-tap. */
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  categoryId: objectIdSchema,
  /** Defaults to today, in the shop's timezone. */
  incurredOn: dateInputSchema.optional(),
  /**
   * Defaults to `incurredOn` — the overwhelmingly common case, where a shop pays as it buys.
   * Pass `null` explicitly for a bill that has been received and not yet settled.
   */
  paidOn: dateInputSchema.nullish(),
  vendor: z.string().trim().max(EXPENSE_VENDOR_MAX_LENGTH).nullish(),
  method: paymentMethodSchema.default('cash'),
  note: z.string().trim().max(EXPENSE_NOTE_MAX_LENGTH).nullish(),
  billMediaIds: z.array(objectIdSchema).max(10).default([]),
});
export type RecordExpenseInput = z.input<typeof recordExpenseSchema>;

export const updateExpenseSchema = z
  .object({
    amountPaise: paiseSchema.min(1, 'Enter an amount').optional(),
    categoryId: objectIdSchema.optional(),
    incurredOn: dateInputSchema.optional(),
    paidOn: dateInputSchema.nullish(),
    vendor: z.string().trim().max(EXPENSE_VENDOR_MAX_LENGTH).nullish(),
    method: paymentMethodSchema.optional(),
    note: z.string().trim().max(EXPENSE_NOTE_MAX_LENGTH).nullish(),
    billMediaIds: z.array(objectIdSchema).max(10).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
export type UpdateExpenseInput = z.input<typeof updateExpenseSchema>;

// --- Listing and drilling down --------------------------------------------------------------

/**
 * The date an expense is filed under for a given basis. One function, called by the list, the
 * P&L and the export, so a drill-down cannot show a different set of rows from the total it
 * was opened from.
 */
export function expensePeriodDate(
  expense: { incurredOn: string; paidOn: string | null },
  basis: PnlBasis,
): string | null {
  return basis === 'cash' ? expense.paidOn : expense.incurredOn;
}

export const expenseQuerySchema = paginationQuerySchema.extend({
  /**
   * The period, given the way the P&L gives it, so tapping a figure lists exactly the rows
   * behind it. `month` + `basis` is the drill-down; `from`/`to` is the free-range list.
   */
  month: monthSchema.optional(),
  basis: z.enum(['cash', 'accrual']).default('accrual'),
  from: dateInputSchema.optional(),
  to: dateInputSchema.optional(),
  categoryId: objectIdSchema.optional(),
  q: z.string().trim().max(120).optional(),
});
export type ExpenseQuery = z.infer<typeof expenseQuerySchema>;

export const expensePageSchema = z.object({
  items: z.array(expenseSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** Every matching rupee, not just this page's — the figure the drill-down must reconcile to. */
  totalPaise: nonNegativePaiseSchema,
});
export type ExpensePage = z.infer<typeof expensePageSchema>;

// --- Recurring ----------------------------------------------------------------------------------

export const RECURRENCE_FREQUENCIES = ['monthly', 'weekly'] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];
export const recurrenceFrequencySchema = z.enum(RECURRENCE_FREQUENCIES);

export const recurringExpenseSchema = z.object({
  id: objectIdSchema,
  label: z.string(),
  categoryId: objectIdSchema,
  categoryLabel: z.string(),
  amountPaise: nonNegativePaiseSchema,
  method: paymentMethodSchema,
  vendor: z.string().nullable(),
  note: z.string().nullable(),
  frequency: recurrenceFrequencySchema,
  /** Monthly: 1-31, clamped to the last day of a short month. */
  dayOfMonth: z.number().int().min(1).max(31).nullable(),
  /** Weekly: 0 = Sunday. */
  weekday: z.number().int().min(0).max(6).nullable(),
  startsOn: isoDateStringSchema,
  endsOn: isoDateStringSchema.nullable(),
  /** The next date this will produce an expense for. */
  nextRunOn: isoDateStringSchema,
  active: z.boolean(),
  lastGeneratedOn: isoDateStringSchema.nullable(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type RecurringExpense = z.infer<typeof recurringExpenseSchema>;

export const recurringExpenseListSchema = z.object({ items: z.array(recurringExpenseSchema) });
export type RecurringExpenseList = z.infer<typeof recurringExpenseListSchema>;

export const createRecurringExpenseSchema = z
  .object({
    label: z.string().trim().min(1, 'Give it a name').max(EXPENSE_CATEGORY_LABEL_MAX_LENGTH),
    categoryId: objectIdSchema,
    amountPaise: paiseSchema.min(1, 'Enter an amount'),
    method: paymentMethodSchema.default('cash'),
    vendor: z.string().trim().max(EXPENSE_VENDOR_MAX_LENGTH).nullish(),
    note: z.string().trim().max(EXPENSE_NOTE_MAX_LENGTH).nullish(),
    frequency: recurrenceFrequencySchema.default('monthly'),
    dayOfMonth: z.coerce.number().int().min(1).max(31).optional(),
    weekday: z.coerce.number().int().min(0).max(6).optional(),
    startsOn: dateInputSchema,
    endsOn: dateInputSchema.nullish(),
  })
  .refine((value) => value.frequency !== 'monthly' || value.dayOfMonth !== undefined, {
    message: 'Say which day of the month it falls on',
    path: ['dayOfMonth'],
  })
  .refine((value) => value.frequency !== 'weekly' || value.weekday !== undefined, {
    message: 'Say which day of the week it falls on',
    path: ['weekday'],
  });
export type CreateRecurringExpenseInput = z.input<typeof createRecurringExpenseSchema>;

export const updateRecurringExpenseSchema = z
  .object({
    label: z.string().trim().min(1).max(EXPENSE_CATEGORY_LABEL_MAX_LENGTH).optional(),
    categoryId: objectIdSchema.optional(),
    amountPaise: paiseSchema.min(1).optional(),
    method: paymentMethodSchema.optional(),
    vendor: z.string().trim().max(EXPENSE_VENDOR_MAX_LENGTH).nullish(),
    note: z.string().trim().max(EXPENSE_NOTE_MAX_LENGTH).nullish(),
    endsOn: dateInputSchema.nullish(),
    active: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
export type UpdateRecurringExpenseInput = z.input<typeof updateRecurringExpenseSchema>;

/**
 * Confirming a generated row before it posts.
 *
 * The amount can be corrected on the way through, because the point of a standing instruction
 * for electricity is the REMINDER, not the figure — the bill is never the same twice.
 */
export const confirmPendingExpenseSchema = z.object({
  amountPaise: paiseSchema.min(1, 'Enter an amount').optional(),
  paidOn: dateInputSchema.nullish(),
  method: paymentMethodSchema.optional(),
  note: z.string().trim().max(EXPENSE_NOTE_MAX_LENGTH).nullish(),
});
export type ConfirmPendingExpenseInput = z.input<typeof confirmPendingExpenseSchema>;

export const pendingExpensesSchema = z.object({
  items: z.array(expenseSchema),
  totalPaise: nonNegativePaiseSchema,
});
export type PendingExpenses = z.infer<typeof pendingExpensesSchema>;

// --- Profit and loss ------------------------------------------------------------------------

/**
 * The two ways of answering "did we make money last month", and why the toggle is explicit.
 *
 *  - `cash`    money that actually MOVED. Revenue is what was received against orders; expenses
 *              are what was paid out. This is the number in the drawer and the bank.
 *  - `accrual` what was EARNED and INCURRED, whether or not it has been settled. Revenue is what
 *              was invoiced; expenses are what was incurred.
 *
 * A shop carrying ₹80,000 of unpaid balances is profitable on one and not the other, and both
 * are true. Picking one silently would hand an owner a number they cannot reconcile against
 * anything they can see.
 */
export const PNL_BASES = ['cash', 'accrual'] as const;
export type PnlBasis = (typeof PNL_BASES)[number];
export const pnlBasisSchema = z.enum(PNL_BASES);

export const pnlQuerySchema = z.object({
  month: monthSchema,
  basis: pnlBasisSchema.default('cash'),
});
export type PnlQuery = z.infer<typeof pnlQuerySchema>;

export const pnlCategoryRowSchema = z.object({
  categoryId: objectIdSchema,
  categoryKey: z.string(),
  categoryLabel: z.string(),
  amountPaise: nonNegativePaiseSchema,
  count: z.number().int().nonnegative(),
  /** Share of the month's expenses, 0-1. Rendered as a bar; never used for arithmetic. */
  share: z.number(),
});
export type PnlCategoryRow = z.infer<typeof pnlCategoryRowSchema>;

export const pnlTotalsSchema = z.object({
  revenuePaise: paiseSchema,
  expensePaise: nonNegativePaiseSchema,
  profitPaise: paiseSchema,
  revenueEntryCount: z.number().int().nonnegative(),
  expenseEntryCount: z.number().int().nonnegative(),
});
export type PnlTotals = z.infer<typeof pnlTotalsSchema>;

export const pnlSchema = z.object({
  month: monthSchema,
  basis: pnlBasisSchema,
  /** Start of the month in the shop's timezone, inclusive. */
  from: isoDateStringSchema,
  /** Start of the next month, exclusive. */
  to: isoDateStringSchema,
  totals: pnlTotalsSchema,
  byCategory: z.array(pnlCategoryRowSchema),
  /** The same figures for the month before, so the screen can show the movement. */
  previous: z.object({
    month: monthSchema,
    totals: pnlTotalsSchema,
  }),
  /** This month minus last, for each headline figure. */
  change: z.object({
    revenuePaise: paiseSchema,
    expensePaise: paiseSchema,
    profitPaise: paiseSchema,
  }),
  /**
   * Unconfirmed recurring rows waiting on the owner. Shown beside the profit as a caveat, never
   * folded into it — the whole point of confirmation is that nothing posts behind their back.
   */
  pendingCount: z.number().int().nonnegative(),
  pendingPaise: nonNegativePaiseSchema,
});
export type Pnl = z.infer<typeof pnlSchema>;

/** One line behind the revenue figure — a payment on the cash basis, a document on the accrual. */
export const pnlRevenueEntrySchema = z.object({
  id: objectIdSchema,
  /** `payment`, `refund`, `reversal` on cash; `invoice`, `credit_note` on accrual. */
  kind: z.string(),
  occurredAt: isoDateStringSchema,
  /** The order number, or the invoice number. */
  reference: z.string(),
  orderId: objectIdSchema.nullable(),
  customerName: z.string(),
  /** Signed: a refund and a credit note are negative, so the column sums to the headline. */
  amountPaise: paiseSchema,
});
export type PnlRevenueEntry = z.infer<typeof pnlRevenueEntrySchema>;

export const pnlRevenueEntriesSchema = z.object({
  month: monthSchema,
  basis: pnlBasisSchema,
  entries: z.array(pnlRevenueEntrySchema),
  /** The sum of the entries above, and therefore of the figure they were opened from. */
  totalPaise: paiseSchema,
});
export type PnlRevenueEntries = z.infer<typeof pnlRevenueEntriesSchema>;
